import { isAxiosError, isCancel } from 'axios';
import type { ApiErrorBody, ErrorCode } from '@/types/api';

/** The single error type every API call rejects with. `status` is 0 when there was no response. */
export class ApiError extends Error {
  override readonly name = 'ApiError';

  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly requestId?: string,
    readonly details?: unknown,
  ) {
    super(message);
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ApiErrorBody).code === 'string' &&
    typeof (value as ApiErrorBody).message === 'string'
  );
}

/** Normalizes anything thrown by axios (or our own code) into an ApiError. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (isCancel(error)) return new ApiError(0, 'CANCELLED', 'Request was cancelled.');

  if (isAxiosError(error)) {
    const response = error.response;
    if (!response) {
      return error.code === 'ECONNABORTED'
        ? new ApiError(0, 'TIMEOUT', 'The server took too long to respond.')
        : new ApiError(0, 'NETWORK_ERROR', 'Unable to reach the server. Check your connection.');
    }
    if (isErrorBody(response.data)) {
      const body = response.data;
      return new ApiError(response.status, body.code, body.message, body.requestId, body.details);
    }
    return new ApiError(
      response.status,
      response.status >= 500 ? 'INTERNAL_ERROR' : 'BAD_REQUEST',
      'Unexpected response from the server.',
    );
  }

  return new ApiError(0, 'INTERNAL_ERROR', 'Something went wrong.');
}
